// Owns Store Action status and inventory-transfer mutations, including query invalidation.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GenerateReceipt, manageStoreActionItems } from '../../../api/user';
import {
  hasSuccessfulStoreActionStatus,
  StoreAction,
  StoreActionStatus,
} from './storeActionDomain';

type StoreActionStatusMutationVariables = {
  readonly item: StoreAction;
  readonly status: StoreActionStatus;
};

type TransferTransactionVariables = {
  readonly item: StoreAction;
  readonly pdf: string;
  readonly type: 'EAITF' | 'EAITT';
  readonly onEnd: () => void;
};

export const useStoreActionStatusMutation = (storeNumber: string) => {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    ReadonlyArray<string>,
    Error,
    StoreActionStatusMutationVariables
  >({
    mutationFn: async ({ item, status }) => {
      const actionId = String(item.storeActionItemId);
      // Completion is the existing backend behavior; reopening is explicitly opt-in.
      const response = await manageStoreActionItems(
        status === 'COMPLETED'
          ? { storeActionItemIds: [actionId], storeNumber }
          : { reopen: true, storeActionItemIds: [actionId], storeNumber }
      );

      if (!hasSuccessfulStoreActionStatus(response)) {
        throw new Error('Store Action status transition failed');
      }

      return response.data?.storeActionItemIds || [];
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['store-actions'] }),
    useErrorBoundary: true,
  });

  const startStatusMutation = (
    item: StoreAction,
    status: StoreActionStatus
  ): boolean => {
    if (item.isVirtual || item.storeActionItemId === null) {
      return false;
    }

    mutation.mutate({ item, status });
    return true;
  };

  return { isPending: mutation.isPending, startStatusMutation };
};

export const useStoreActionTransferTransaction = (storeNumber: string) => {
  const queryClient = useQueryClient();
  const completionMutation = useMutation<
    ReadonlyArray<string>,
    Error,
    TransferTransactionVariables
  >({
    mutationFn: async (transaction) => {
      const response = await manageStoreActionItems({
        storeActionItemIds: [String(transaction.item.storeActionItemId)],
        storeNumber,
      });
      if (!hasSuccessfulStoreActionStatus(response)) {
        throw new Error('Store Action status transition failed');
      }
      return response.data?.storeActionItemIds || [];
    },
    onSuccess: (_data, transaction) => {
      transaction.onEnd();
      return queryClient.invalidateQueries({ queryKey: ['store-actions'] });
    },
    useErrorBoundary: true,
  });

  const uploadMutation = useMutation<void, Error, TransferTransactionVariables>(
    {
      mutationFn: async (transaction) => {
        const response = await GenerateReceipt({
          action: 4,
          documents: [
            {
              documentType: transaction.type === 'EAITF' ? 'TOF' : 'TIF',
              file: transaction.pdf,
            },
          ],
          inventoryId: transaction.item.inventoryId as string | number,
        });
        if (!hasSuccessfulStoreActionStatus(response)) {
          throw new Error('Transfer receipt upload failed');
        }
      },
      onSuccess: (_data, transaction) => completionMutation.mutate(transaction),
      useErrorBoundary: true,
    }
  );

  const startTransferTransaction = async (
    variables: TransferTransactionVariables
  ): Promise<boolean> => {
    if (variables.item.storeActionItemId === null || variables.item.isVirtual) {
      return false;
    }

    uploadMutation.mutate(variables);
    return true;
  };

  return { startTransferTransaction };
};
